/**
 * The widget's inline SVG icons. All decorative: every button that shows one
 * carries its own aria-label, so the SVGs are hidden from assistive tech.
 */
import type { ReactNode } from 'react';

interface IconProps {
  size?: number;
  strokeWidth?: number;
}

function Svg({ size = 16, strokeWidth = 2, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function ChatIcon(props: IconProps) {
  return (
    <Svg size={24} strokeWidth={1.8} {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </Svg>
  );
}

/** The header's info button: the one glyph a visitor reads without a label. */
export function InfoIcon(props: IconProps) {
  return (
    <Svg size={15} {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="11" x2="12" y2="16" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </Svg>
  );
}

/** The info panel's "Send feedback" entry: the chat bubble with lines of text in it. */
export function FeedbackIcon(props: IconProps) {
  return (
    <Svg size={14} {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="8" y1="9" x2="16" y2="9" />
      <line x1="8" y1="13" x2="13" y2="13" />
    </Svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </Svg>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <Svg size={14} {...props}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Svg size={14} strokeWidth={2.5} {...props}>
      <polyline points="20 6 9 17 4 12" />
    </Svg>
  );
}

export function ExpandIcon(props: IconProps) {
  return (
    <Svg size={14} {...props}>
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </Svg>
  );
}

export function CollapseIcon(props: IconProps) {
  return (
    <Svg size={14} {...props}>
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </Svg>
  );
}

export function SendIcon(props: IconProps) {
  return (
    <Svg size={18} {...props}>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </Svg>
  );
}

export function RetryIcon(props: IconProps) {
  return (
    <Svg size={13} {...props}>
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </Svg>
  );
}

/**
 * The standing calls to action (1.18.0). These two chips are the only ones
 * that carry an icon: it is what separates them from a suggestion at a glance,
 * before a single word has been read.
 */

/** "Book an appointment": a calendar. */
export function CalendarIcon(props: IconProps) {
  return (
    <Svg size={14} {...props}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="3" x2="8" y2="7" />
      <line x1="16" y1="3" x2="16" y2="7" />
    </Svg>
  );
}

/** "Leave your details": a person. */
export function PersonIcon(props: IconProps) {
  return (
    <Svg size={14} {...props}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </Svg>
  );
}

/** The language line: a globe, drawn with the meridians that read as one at 12px. */
export function GlobeIcon(props: IconProps) {
  return (
    <Svg size={12} strokeWidth={1.8} {...props}>
      <circle cx="12" cy="12" r="9" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
    </Svg>
  );
}
